<script setup lang="ts">
    import { useAskMeStore } from '../../stores/ask-me';
    import { __ } from '../../stores/expressions';
    import TextChatMessage from './TextChatMessage.vue';
    import { nextTick, ref, watch } from 'vue';
    import SuggestedChatMessage from './SuggestedChatMessage.vue';
    import ListChatMessage from './ListChatMessage.vue';
    import { useChildWidgetUpdated } from '@/js/composables/useWidgetUpdated';
    import ChartChatMessage from './ChartChatMessage.vue';
    import { IChatMessage } from '@/js/services/api/chat/chat';
    import ErrorChatMessage from './ErrorChatMessage.vue';
    import ClarificationChatMessage from './ClarificationChatMessage.vue';
    import UpdateChatMessage from './UpdateChatMessage.vue';

    const props = withDefaults(
        defineProps<{
            module?: boolean;
        }>(),
        {
            module: false,
        }
    );

    const ask_me = useAskMeStore();
    const container = ref<HTMLDivElement | null>(null);

    const scrollToPosition = (position: number, relative: boolean = false) => {
        if (container.value) {
            if (relative) {
                position = container.value.scrollTop + position;
            }

            container.value.scrollTo({
                top: position,
            });
        }
    };

    watch(
        () => ask_me.messages,
        (messages) => {
            nextTick(() => {
                scrollToPosition(container.value?.scrollHeight || 0);
            });
        },
        { deep: true, immediate: true }
    );

    useChildWidgetUpdated(() => {
        scrollToPosition(container.value?.scrollHeight || 0);
    });

    const chatMessageTemplate = (message: IChatMessage) => {
        switch (message.messageType) {
            case 'Text':
                return TextChatMessage;
            case 'List':
                return ListChatMessage;
            case 'Chart':
                return ChartChatMessage;
            case 'Prompt':
                return SuggestedChatMessage;
            case 'Error':
                return ErrorChatMessage;
            case 'Clarification':
                return ClarificationChatMessage;
            case 'Update':
                return UpdateChatMessage;
            default:
                return TextChatMessage;
        }
    };
</script>

<template>
    <div
        ref="container"
        class="flex-grow overflow-y-scroll z-0 custom-scrollbar"
        :class="props.module ? 'h-full' : 'max-h-screen'"
    >
        <div
            :class="props.module ? 'px-6' : 'px-10 2xl:px-20 mx-auto max-w-400'"
            role="log"
            aria-live="polite"
            aria-relevant="additions"
            :aria-label="__('messages')"
        >
            <ul :class="props.module ? 'pt-4 pb-1' : 'pt-8 pb-38'">
                <component
                    :is="chatMessageTemplate(message)"
                    v-for="(message, index) in ask_me.messages"
                    :key="message.id"
                    :message="message"
                    :is-last="index === ask_me.messages.length - 1"
                    @scroll-to="scrollToPosition($event, true)"
                />
            </ul>
        </div>
    </div>
</template>
